/**
 * Committee Import Template — parse, validate, duplicate detection
 */

import { COMMITTEE_TYPES, normalizeCommitteeTypeCode, committeeTypeLabel } from '../constants/committeeTypes';

export const COMMITTEE_TEMPLATE_COLUMNS = [
  { key: 'companyName', label: 'Company Name', required: true, example: 'Etisalat Group PJSC' },
  { key: 'name', label: 'Committee Name', required: true, example: 'Audit Committee' },
  { key: 'type', label: 'Committee Type', required: true, example: 'AUDIT', note: 'Required (e.g. AUDIT, RISK, NOMINATION)' },
  { key: 'quorum', label: 'Quorum', required: false, example: '50%', note: 'Optional (% or headcount, default 50%)' },
  { key: 'chairperson', label: 'Chairperson', required: false, example: 'Jane Doe' },
  { key: 'status', label: 'Status', required: false, example: 'ACTIVE', note: 'Optional (ACTIVE, INACTIVE, PENDING)' },
];

function escapeCsvCell(value) {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function generateCommitteeTemplateData() {
  const headers = COMMITTEE_TEMPLATE_COLUMNS.map((col) => col.label);
  const examples = COMMITTEE_TEMPLATE_COLUMNS.map((col) => col.example);
  const notes = COMMITTEE_TEMPLATE_COLUMNS.map((col) => {
    if (col.note) return col.note;
    return col.required ? 'Required' : 'Optional';
  });

  return { headers, examples, notes, columns: COMMITTEE_TEMPLATE_COLUMNS };
}

export function downloadCommitteeTemplate() {
  const { headers, examples, notes } = generateCommitteeTemplateData();
  const csvContent = [
    headers.map(escapeCsvCell).join(','),
    examples.map(escapeCsvCell).join(','),
    notes.map(escapeCsvCell).join(','),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `committee_import_template_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function safeString(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).trim();
}

function getCell(row, label, key) {
  return row[label] ?? row[key];
}

function parseQuorum(value) {
  const raw = safeString(value);
  if (!raw) {
    return { quorumType: 'PERCENTAGE', quorumPercentage: 50, quorumNumber: '' };
  }
  if (raw.includes('%')) {
    const pct = parseInt(raw.replace('%', ''), 10);
    return {
      quorumType: 'PERCENTAGE',
      quorumPercentage: Number.isFinite(pct) ? pct : 50,
      quorumNumber: '',
    };
  }
  const num = parseInt(raw, 10);
  if (Number.isFinite(num) && String(num) === raw.replace(/\s/g, '')) {
    return { quorumType: 'NUMBER', quorumNumber: String(num), quorumPercentage: 50 };
  }
  return { quorumType: 'PERCENTAGE', quorumPercentage: 50, quorumNumber: '' };
}

export function parseImportedCommitteeRow(row, index) {
  const quorumFields = parseQuorum(getCell(row, 'Quorum', 'quorum'));
  return {
    _rowIndex: index,
    companyName: safeString(getCell(row, 'Company Name', 'companyName')),
    name: safeString(getCell(row, 'Committee Name', 'name')),
    type: normalizeCommitteeTypeCode(getCell(row, 'Committee Type', 'type')),
    quorum: safeString(getCell(row, 'Quorum', 'quorum')),
    ...quorumFields,
    chairperson: safeString(getCell(row, 'Chairperson', 'chairperson')),
    status: safeString(getCell(row, 'Status', 'status')).toUpperCase() || 'ACTIVE',
  };
}

export function isEmptyCommitteeRow(row) {
  return !row.companyName && !row.name && !row.type;
}

export function isCommitteeTemplateHelperRow(row) {
  const name = safeString(row.name);
  if (/^(Required|Optional)(\s|$|\()/i.test(name)) {
    return true;
  }
  const { examples } = generateCommitteeTemplateData();
  if (row.companyName === examples[0] && name === examples[1]) {
    return true;
  }
  return false;
}

export function validateImportedCommitteeRow(row, { committeeTypes = [] } = {}) {
  const errors = [];
  const validStatuses = ['ACTIVE', 'INACTIVE', 'PENDING'];

  if (!row.companyName) errors.push('Company Name is required');
  if (!row.name) errors.push('Committee Name is required');
  if (!row.type) {
    errors.push('Committee Type is required');
  }

  const types = committeeTypes.length ? committeeTypes : COMMITTEE_TYPES;
  if (row.type && !types.some((t) => String(t.code).toUpperCase() === row.type)) {
    errors.push(`Unknown Committee Type "${row.type}"`);
  }

  if (row.status && !validStatuses.includes(row.status)) {
    errors.push('Status must be ACTIVE, INACTIVE, or PENDING');
  }

  if (row.quorumType === 'PERCENTAGE') {
    const pct = row.quorumPercentage;
    if (pct < 1 || pct > 100) {
      errors.push('Quorum percentage must be between 1 and 100');
    }
  } else if (row.quorumType === 'NUMBER' && !row.quorumNumber) {
    errors.push('Quorum number is required when not using percentage');
  }

  return {
    isValid: errors.length === 0,
    errors,
    typeLabel: committeeTypeLabel(row.type),
  };
}

export function buildCommitteeCreatePayload(committee) {
  return {
    companyId: Number(committee.companyId),
    name: committee.name,
    type: committee.type,
    quorumType: committee.quorumType,
    quorumNumber: committee.quorumNumber,
    quorumPercentage: committee.quorumPercentage,
    chairperson: committee.chairperson || null,
    status: committee.status || 'ACTIVE',
  };
}

export function calculateCommitteeSimilarityScore(imported, existing) {
  let score = 0;
  let maxScore = 0;

  if (imported.companyId && existing.companyId) {
    maxScore += 30;
    if (Number(imported.companyId) === Number(existing.companyId)) {
      score += 30;
    }
  } else if (imported.companyName && existing.company) {
    maxScore += 30;
    const a = imported.companyName.toLowerCase();
    const b = existing.company.toLowerCase();
    if (a === b) score += 30;
    else if (a.includes(b) || b.includes(a)) score += 15;
  }

  if (imported.name && existing.name) {
    maxScore += 50;
    const a = imported.name.toLowerCase();
    const b = existing.name.toLowerCase();
    if (a === b) score += 50;
    else if (a.includes(b) || b.includes(a)) score += 25;
  }

  if (imported.type && (existing.typeCode || existing.type)) {
    maxScore += 20;
    const a = normalizeCommitteeTypeCode(imported.type);
    const b = normalizeCommitteeTypeCode(existing.typeCode || existing.type);
    if (a === b) score += 20;
  }

  if (maxScore === 0) return 0;
  return Math.round((score / maxScore) * 100);
}

export { getMatchConfidenceLevel } from './companyImportTemplate';
